import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BackButton } from "../../../../components/buttons/backButton";
import { Button } from "../../../../components/buttons/button";
import {
  EventCard,
  EventCardSkeleton,
} from "../../../../components/event/eventCard";
import { TabNav } from "../../../../components/tabNav";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useAdminGetEventGuestsQuery,
  useAdminGetEventTicketsBreakdownQuery,
  useAdminGetEventTicketsQuery,
} from "../../../../redux/features/admin/event/events.api";
import { useGetEventQuery } from "../../../../redux/features/event/event.api";
import { TICKET_COLOR_OPTIONS } from "../../../../utils/constants";
import {
  formatDate,
  formatTime,
  getEventUrl,
  getRandomColor,
  parseTimeToDate,
} from "../../../../utils/helpers";
import { TicketSalesBreakdown } from "../../../../utils/types";
import { EventGuests } from "../../../events/view/components/eventGuests";
import { AdminEventOverview } from "./components/overview";
import { AdminEventTickets } from "./components/tickets";

export type Tabs = "overview" | "guests" | "tickets";

export const AdminViewSingleEvent = ({
  guestView = false,
}: {
  guestView?: boolean;
}) => {
  const [activeTab, setActiveTab] = useState<Tabs>("overview");
  const [isPaidEvent, setIsPaidEvent] = useState<boolean>(false);
  const [ticketBreakdown, setTicketBreakdown] = useState<TicketSalesBreakdown>(
    [],
  );

  const tabList = [
    {
      title: "Overview",
      to: "overview",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Guests",
      to: "guests",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Tickets",
      to: "tickets",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
  ];

  const { id } = useParams<{ id: string }>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: eventResponse,
    isLoading: fetchingEvenntDetails,
    refetch: refetchEventDetails,
  } = useGetEventQuery(
    {
      id: id,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: ticketPurchaseBreakdown } =
    useAdminGetEventTicketsBreakdownQuery(
      {
        id: id!,
      },
      { refetchOnMountOrArgChange: true, skip: !id || guestView },
    );

  const { data: guests, isFetching: fetchingGuests } =
    useAdminGetEventGuestsQuery(
      {
        id: id!,
        search: "",
        page: 1,
        limit: 10,
        sortOrder: "desc",
      },
      { refetchOnMountOrArgChange: true, skip: !id || guestView },
    );

  const { data: ticketsData, isFetching: fetchingTickets } =
    useAdminGetEventTicketsQuery(
      {
        id: id!,
      },
      { refetchOnMountOrArgChange: true, skip: !id || guestView },
    );

  useEffect(() => {
    setIsPaidEvent(
      ticketsData?.data?.tickets?.some((t) => t.ticketType === "paid") ?? false,
    );
  }, [ticketsData]);

  useEffect(() => {
    const selectedColors: string[] = [];

    if (ticketPurchaseBreakdown?.data) {
      setTicketBreakdown(
        ticketPurchaseBreakdown.data.map((t) => {
          const color = getRandomColor(
            TICKET_COLOR_OPTIONS,
            selectedColors,
            "sequential",
          );
          selectedColors.push(color);
          return {
            ticketId: t.ticketId,
            purchaseQuantity: t.totalQuantityPurchased,
            color: color,
          };
        }),
      );
    }
  }, [ticketPurchaseBreakdown]);

  return fetchingEvenntDetails ? (
    <EventCardSkeleton imageDimension={300} showExtraDetails className="" />
  ) : (
    <div className="flex w-full animate-fade-in flex-col gap-2">
      <BackButton />

      <EventCard
        image={eventResponse?.data?.image!}
        name={eventResponse?.data?.name!}
        location={eventResponse?.data?.location!}
        startDate={formatDate(
          eventResponse?.data?.startDate!,
          "ddd, MMMM DD, YYYY",
        )}
        endDate={formatDate(
          eventResponse?.data?.endDate!,
          "ddd, MMMM DD, YYYY",
        )}
        startTime={formatTime(parseTimeToDate(eventResponse?.data?.startTime!))}
        endTime={formatTime(parseTimeToDate(eventResponse?.data?.endTime!))}
        noTickets={undefined}
        totalGuests={guests?.data?.totalCount ?? 0}
        isOrganizer={eventResponse?.data?.userType === "organiser"}
        event={eventResponse?.data!}
        className=""
        imageDimension={300}
        hasPaidTickets={isPaidEvent}
        showUserTypePill={guestView}
        showStatusPill
        showLocationPill
        showTicketPill={!guestView}
        showCreatedOn
      >
        {!guestView && (
          <div className="flex flex-row items-center gap-[10px]">
            <Button
              type="button"
              onClick={() => {
                window.open(getEventUrl(eventResponse?.data?.slug!), "_self");
              }}
              text="Go To Event"
              icon="share"
              variation="plain-grey"
              className=""
              disabled={false}
            />

            <Button
              type="button"
              onClick={() => {
                handleModalOpen("flagEventModal");
              }}
              text="Flag Event"
              icon="flag"
              variation="plain-grey"
              className="hidden md:flex"
              disabled={false}
            />
          </div>
        )}
      </EventCard>

      <div className="flex flex-col gap-[2px]">
        {guestView ? (
          <BgWrapper>
            <MiniHeader header="Guests" />
          </BgWrapper>
        ) : (
          <BgWrapper className="!rounded-b-none">
            <TabNav
              navs={tabList}
              activeTab={activeTab}
              variant="switcher"
              className=""
              tabClassName=""
            />
          </BgWrapper>
        )}

        {activeTab === "overview" && !guestView ? (
          <AdminEventOverview
            event={eventResponse?.data!}
            setTab={setActiveTab}
            ticketsData={ticketsData}
            guests={guests}
            loading={fetchingGuests || fetchingEvenntDetails || fetchingTickets}
          />
        ) : undefined}

        {activeTab === "guests" &&
        !guestView &&
        eventResponse?.data !== undefined ? (
          <EventGuests
            event={eventResponse?.data!}
            ticketBreakdown={ticketBreakdown}
            adminView={false}
          />
        ) : undefined}

        {activeTab === "tickets" &&
        !guestView &&
        eventResponse?.data !== undefined ? (
          <AdminEventTickets
            event={eventResponse?.data!}
            data={ticketsData}
            fetchingTickets={fetchingTickets}
          />
        ) : undefined}
      </div>
    </div>
  );
};
